import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { EmailNotificationPDFModel, GenericDocContainerModel, EmailNotificationModel } from '../../../../../../models/aggregates/generic_doc';
import { ApiVisa, BaseApiTemplateService, BasicApiResponseModel, Core2Config, Pagination, PagingResponseModel } from '../../shared';
import { BaseGenDocTypeService } from './base-generic-doc-hdr.service';
import { GenericDocumentPutDTO } from '../../../data-transfer-objects';

export class ConversionBodyRequest {
  original_generic_doc_hdr_guids: string[];
  new_server_doc_type: string;
  validateStockBalance?: boolean;
  validateStockBalanceForSignumZero?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class InternalSalesOrderService extends BaseGenDocTypeService {

  constructor(http: HttpClient) {
    super(http, 'internal-sales-orders');
  }

  getLinesByCriteria(pagination: Pagination, visa: ApiVisa) {
    if (this.endpoint_path && visa.api_domain_url) {
      this.apiUrl = `${visa.api_domain_url}${this.endpoint_path}/type`;
    }
    const UrlByCriteria = this.apiUrl + '/query?' + pagination.getQueryKeyValues();
    return this.httpClient.get<PagingResponseModel<GenericDocContainerModel>>(UrlByCriteria, this.getHttpHeader(visa));
  }

  printPdf(guid: string, templateDocId: string, printServiceClassName: string, apiVisa: ApiVisa): Observable<Blob> {
    return this.httpClient.get<Blob>(
      this.apiUrl + '/print-pdf/' + guid,
      {
        headers: new HttpHeaders({
          authorization: apiVisa.jwt_secret,
          responseType: 'blob',
          tenantCode: apiVisa.tenantCode
        }),
        params: new HttpParams()
          .set('templateDocId', templateDocId)
          .set('printServiceClassName', printServiceClassName),
        responseType: 'blob' as 'json'
      }
    );
  }

  public printJasperPdf(
    guid: string,
    printServiceClassName: string,
    printableFormatGuid: string,
    apiVisa: ApiVisa,
    customParam?: string,
  ): Observable<Blob> {
    let params;
    let url;
    if (customParam) {
      params = new HttpParams()
        .set('printServiceClassName', printServiceClassName)
        .set('printableFormatHdrGuid', printableFormatGuid)
        .set('customParam', customParam);
    } else {
      params = new HttpParams()
        .set('printServiceClassName', printServiceClassName)
        .set('printableFormatHdrGuid', printableFormatGuid);
    }
    url = apiVisa.api_domain_url + this.endpoint_path + '/print-jasper-pdf/' + guid;
    return this.httpClient.get<Blob>(
      url,
      this.getHttpHeaderBlob(apiVisa, params)
    );
  }

  public printDeliveryItemsJasperPdf(
    guids: string[],
    printServiceClassName: string,
    printableFormatGuid: string,
    apiVisa: ApiVisa,
    customParam?: string,
  ): Observable<Blob> {
    let params;
    let dtoObject;
    let url;

    if (customParam) {
      params = new HttpParams()
        .set('printServiceClassName', printServiceClassName)
        .set('printableFormatHdrGuid', printableFormatGuid)
        .set('customParam', customParam);
    } else {
      params = new HttpParams()
        .set('printServiceClassName', printServiceClassName)
        .set('printableFormatHdrGuid', printableFormatGuid);
    }
    url = apiVisa.api_domain_url + this.endpoint_path + '/print-jasper-pdf/' + guids;
    return this.httpClient.post<Blob>(
      url,
      dtoObject,
      this.getHttpHeaderBlob(apiVisa, params)
    );
  }

  getByPgwMerchantCodeMerchantRefNoLoginEntity(merchantCode: string, merchantRefNo: string, visa: ApiVisa) {
    if (this.endpoint_path && visa.api_domain_url) {
      this.apiUrl = `${visa.api_domain_url}${this.endpoint_path}`;
    }
    const url = this.apiUrl + '/login-entity-ep/by-merchant-code-ref-no?merchantCode=' + merchantCode + '&merchantRefNo='  + merchantRefNo;
    return this.httpClient.get<BasicApiResponseModel<GenericDocContainerModel>>(url, this.getHttpHeader(visa));
  }

  sendEmailPDF(guid, dtoObject: EmailNotificationPDFModel, visa: ApiVisa) {
    let url;

    if (this.endpoint_path && visa.api_domain_url) {
      url =
        visa.api_domain_url +
        this.endpoint_path +
        "/send-email-pdf/backoffice-ep/" +
        guid;
    }

    return this.httpClient.post(url, dtoObject, this.getHttpHeader(visa));
  }

  sendEmail(dtoObject: EmailNotificationPDFModel, visa: ApiVisa) {
    let url;

    if (this.endpoint_path && visa.api_domain_url) {
      url =
        visa.api_domain_url +
        this.endpoint_path +
        "/send-email/backoffice-ep/"
    }

    return this.httpClient.post(url, dtoObject, this.getHttpHeader(visa));
  }

  sendEmailNotification(
    guid,
    dtoObject: EmailNotificationModel,
    visa: ApiVisa
  ) {
    let url;

    if (this.endpoint_path && visa.api_domain_url) {
      url =
        visa.api_domain_url +
        this.endpoint_path +
        "/email-notification/backoffice-ep/" +
        guid;
    }

    return this.httpClient.post(url, dtoObject, this.getHttpHeader(visa));
  }

  updateWithParams(dto: GenericDocumentPutDTO, genDocCon: any, visa: ApiVisa) {
    let url;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + "/with-params?generateTrackingId=" + dto.generateTrackingId + "&trackingIdPrefix=" + dto.trackingIdPrefix;
    }
    return this.httpClient.put<BasicApiResponseModel<GenericDocContainerModel>>(
      url,
      genDocCon,
      this.getHttpHeader(visa)
    );
  }

  convertToSalesInvoice(
    body: ConversionBodyRequest,
    visa: ApiVisa
  ): Observable<BasicApiResponseModel<GenericDocContainerModel>> {
    const path = 'core2/tnt/dm/erp/gen-doc/convert-documents/backoffice-ep';
    const domain = visa.api_domain_url ?? Core2Config.DOMAIN_URL;
    const url = domain.endsWith('/') ? domain + path : domain + '/' + path;

    return this.httpClient.post<BasicApiResponseModel<GenericDocContainerModel>>(
      url,
      body,
      this.getHttpHeader(visa)
    );
  }

   public printJasperPdfLoginEntity(
    guid: string,
    printServiceClassName: string,
    printableFormatGuid: string,
    apiVisa: ApiVisa,
    customParam?: string,
  ): Observable<Blob> {
    let params;
    let url;
    if (customParam) {
      params = new HttpParams()
        .set('printServiceClassName', printServiceClassName)
        .set('printableFormatHdrGuid', printableFormatGuid)
        .set('customParam', customParam);
    } else {
      params = new HttpParams()
        .set('printServiceClassName', printServiceClassName)
        .set('printableFormatHdrGuid', printableFormatGuid);
    }
    url = apiVisa.api_domain_url + this.endpoint_path + '/print-jasper-pdf/login-entity-ep/' + guid;
    return this.httpClient.get<Blob>(
      url,
      this.getHttpHeaderBlob(apiVisa, params)
    );
  }
}
